import { SaveDialogContent, SaveForm, SaveButton } from "@/components/SaveForm";
import { useCallback, useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtDateTime, num } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ClipboardList, Plus, Check } from "lucide-react";

export default function Inventory() {
  const { can } = useAuth();
  const [invs, setInvs] = useState(null);
  const [showDeleted, setShowDeleted] = useState(false);
  const [lifecycle, setLifecycle] = useState(null);
  const [lifecycleBusy, setLifecycleBusy] = useState(false);
  const saveLifecycle = async () => {
    if (!lifecycle || lifecycleBusy) return;
    setLifecycleBusy(true);
    try {
      await api.post(`/inventories/${lifecycle.id}/${lifecycle.deleted_at ? "restore" : "delete"}`,
        { expected_revision: lifecycle.write_revision || 0 });
      toast.success(lifecycle.deleted_at ? "Inventário restaurado. Estoque preservado." : "Inventário movido para Excluídos. Estoque preservado.");
      setLifecycle(null); load();
    } catch (e) { toast.error(apiErr(e)); } finally { setLifecycleBusy(false); }
  };
  const [parts, setParts] = useState([]);
  const [countOpen, setCountOpen] = useState(null);
  const [partId, setPartId] = useState("");
  const [qtd, setQtd] = useState("");
  const [revision, setRevision] = useState(null);
  const [revisedQty, setRevisedQty] = useState("");
  const [reason, setReason] = useState("");
  const [revisionBusy, setRevisionBusy] = useState(false);
  const openRevision = (item, action) => {
    setRevision({ item, action }); setRevisedQty(String(item.quantidade_fisica)); setReason("");
  };
  const closeRevision = () => { if (!revisionBusy) setRevision(null); };
  const saveRevision = async () => {
    if (revisionBusy || !revision) return;
    setRevisionBusy(true);
    try {
      const body = revision.action === "correct" ? { quantidade_fisica: Number(revisedQty), motivo: reason } : { motivo: reason };
      await api.post(`/inventories/items/${revision.item.id}/${revision.action}`, body);
      toast.success(revision.action === "correct" ? "Contagem corrigida. O estoque não foi alterado." : "Contagem cancelada. Histórico preservado.");
      setRevision(null); load();
    } catch (e) { toast.error(apiErr(e)); } finally { setRevisionBusy(false); }
  };

  const load = useCallback(() => api.get(showDeleted ? "/inventories?deleted=true" : "/inventories").then((r) => setInvs(Array.isArray(r.data) ? r.data.filter((inv) => inv.modelo !== 2) : r.data)), [showDeleted]);
  useEffect(() => { load(); api.get("/parts").then((r) => setParts(r.data)); }, [load]);

  const create = async () => {
    try { await api.post("/inventories", { descricao: `Inventário ${new Date().toLocaleDateString("pt-BR")}` }); toast.success("Inventário iniciado."); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const submitCount = async () => {
    if (!partId || qtd === "") { toast.error("Selecione a peça e informe a quantidade."); return; }
    try { await api.post(`/inventories/${countOpen}/count`, { part_id: partId, quantidade_fisica: Number(qtd) }); toast.success("Contagem registrada."); setPartId(""); setQtd(""); setCountOpen(null); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const approve = async (itemId) => {
    try { await api.post(`/inventories/items/${itemId}/approve`); toast.success("Divergência aprovada e estoque ajustado."); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  if (invs === null) return <div className="space-y-4"><Skeleton className="h-8 w-64 bg-slate-800" /><Skeleton className="h-96 bg-slate-800" /></div>;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div><h1 className="font-display text-3xl font-bold text-slate-50">Inventário (modelo antigo)</h1><p className="text-slate-400 text-sm">Contagem física por peça/QR. Divergências não ajustam automaticamente — exigem aprovação.</p></div>
        {!showDeleted && can("ALMOXARIFADO") && <Button data-testid="btn-new-inventory" onClick={create} className="bg-sky-700 hover:bg-sky-600"><Plus className="h-4 w-4 mr-1" /> Novo inventário</Button>}
      </div>

      {can("ADMINISTRADOR") && <div className="flex gap-2" aria-label="Visualização de inventários">
        <Button variant={showDeleted ? "outline" : "default"} onClick={() => setShowDeleted(false)}>Ativos</Button>
        <Button variant={showDeleted ? "default" : "outline"} onClick={() => setShowDeleted(true)}>Excluídos</Button>
      </div>}
      {showDeleted && <p className="text-sm text-slate-300">Inventários excluídos preservam contagens e movimentos. Restaure para voltar a trabalhar neles.</p>}
      {invs.length === 0 && <div className="text-center py-16 text-slate-500"><ClipboardList className="h-12 w-12 mx-auto mb-3 opacity-50" />Nenhum inventário criado.</div>}

      {invs.map((inv) => (
        <Card key={inv.id} className="card-metal p-5">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div><div className="font-display text-lg font-semibold text-slate-100">{inv.descricao}</div><div className="text-xs text-slate-500">{fmtDateTime(inv.created_at)} · {inv.usuario_nome}</div></div>
            <div className="flex flex-wrap gap-2">
            {!inv.deleted_at && can("ALMOXARIFADO") && <Button data-testid={`btn-count-${inv.id}`} size="sm" variant="secondary" onClick={() => setCountOpen(inv.id)}><Plus className="h-4 w-4 mr-1" /> Contar peça</Button>}
            {can("ADMINISTRADOR") && <Button variant="outline" onClick={() => setLifecycle(inv)}
              className={inv.deleted_at ? "min-h-10 border-sky-400 bg-sky-950 text-sky-100" : "min-h-10 border-rose-400 bg-rose-950 text-rose-100"}>
              {inv.deleted_at ? "Restaurar inventário" : "Excluir inventário"}
            </Button>}
            </div>
          </div>
          {inv.itens?.length > 0 && (
            <div className="mt-4 overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="bg-slate-950 text-slate-400 text-xs uppercase font-mono">
                  <th className="text-left px-3 py-2">Peça</th><th className="text-right px-3 py-2">Sistema</th><th className="text-right px-3 py-2">Físico</th><th className="text-right px-3 py-2">Diferença</th><th className="text-center px-3 py-2">Status</th><th></th>
                </tr></thead>
                <tbody>
                  {inv.itens.map((it) => (
                    <tr key={it.id} className="border-t border-slate-800">
                      <td className="px-3 py-2 text-slate-200">{it.part_desc}</td>
                      <td className="px-3 py-2 text-right font-mono text-slate-300">{it.quantidade_sistema}</td>
                      <td className="px-3 py-2 text-right font-mono text-slate-100">{it.quantidade_fisica}</td>
                      <td className={`px-3 py-2 text-right font-mono ${it.diferenca === 0 ? "text-slate-400" : it.diferenca > 0 ? "text-emerald-300" : "text-rose-300"}`}>{it.diferenca > 0 ? "+" : ""}{it.diferenca}</td>
                      <td className="px-3 py-2 text-center"><span className={`rounded px-2 py-0.5 text-xs ${it.status === "APROVADO" ? "bg-emerald-950/60 text-emerald-300" : it.status === "DIVERGENTE" ? "bg-rose-950/60 text-rose-300" : "bg-slate-800 text-slate-300"}`}>{it.status}</span></td>
                      <td className="px-3 py-2"><div className="flex flex-wrap items-center justify-end gap-2">
                        {!inv.deleted_at && ["OK", "DIVERGENTE"].includes(it.status) && !it.aprovado && can("ALMOXARIFADO") && <>
                          {it.status === "DIVERGENTE" && <Button size="sm" onClick={() => approve(it.id)} className="min-h-10 bg-emerald-700 hover:bg-emerald-600 text-white"><Check className="h-3.5 w-3.5 mr-1" />Aprovar</Button>}
                          <Button size="sm" variant="outline" onClick={() => openRevision(it, "correct")} className="min-h-10 border-sky-400 bg-sky-950 text-sky-100 hover:bg-sky-900 hover:text-white">Corrigir</Button>
                          <Button size="sm" variant="outline" onClick={() => openRevision(it, "cancel")} className="min-h-10 border-rose-400 bg-rose-950 text-rose-100 hover:bg-rose-900 hover:text-white">Cancelar contagem</Button>
                        </>}
                      </div>{it.status === "SUBSTITUIDO" && <p className="mt-1 text-xs text-slate-400">Histórico: substituída por correção.</p>}
                      {it.status === "CANCELADO" && <p className="mt-1 text-xs text-slate-400">Histórico: cancelada, sem ajuste de estoque.</p>}
                      {it.motivo_revisao && <p className="mt-1 text-xs text-slate-400">Motivo: {it.motivo_revisao}</p>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      ))}

      <Dialog open={!!lifecycle} onOpenChange={value => { if (!value && !lifecycleBusy) setLifecycle(null); }}>
        <DialogContent className="bg-slate-900 border-slate-700 text-slate-100" onKeyDown={e => { if (e.key === "Enter") e.preventDefault(); }}>
          <DialogHeader><DialogTitle>{lifecycle?.deleted_at ? "Restaurar inventário?" : "Excluir inventário?"}</DialogTitle></DialogHeader>
          <p>{lifecycle?.descricao}</p>
          <p>{lifecycle?.deleted_at ? "O inventário voltará para Ativos, com todo o histórico preservado." : "O inventário irá para Excluídos e poderá ser restaurado por um administrador. Novas contagens e aprovações ficarão bloqueadas."}</p>
          <p className="font-medium text-amber-200">O estoque não será revertido. Ajustes já aprovados permanecem no estoque ({lifecycle?.itens?.filter(it => it.aprovado || it.status === "APROVADO").length || 0} contagens aprovadas).</p>
          <DialogFooter><Button variant="ghost" disabled={lifecycleBusy} onClick={() => setLifecycle(null)}>Voltar</Button>
            <Button disabled={lifecycleBusy} onClick={saveLifecycle} className={lifecycle?.deleted_at ? "bg-sky-700 text-white" : "bg-rose-700 text-white"}>
              {lifecycleBusy ? "Salvando..." : lifecycle?.deleted_at ? "Confirmar restauração" : "Confirmar exclusão"}
            </Button></DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={revision?.action === "correct"} onOpenChange={value => { if (!value) closeRevision(); }}>
        <SaveDialogContent saveBlocked={revisionBusy} className="bg-slate-900 border-slate-700 text-slate-100">
          <SaveForm disabled={revisionBusy} onSave={saveRevision} className="space-y-4">
            <DialogHeader><DialogTitle>Corrigir contagem pendente</DialogTitle></DialogHeader>
            <p>{revision?.item.part_desc}: a versão anterior será preservada. O estoque só muda após aprovação.</p>
            <div><Label htmlFor="inventory-revised-qty">Quantidade física correta</Label><Input id="inventory-revised-qty" type="number" min="0" step="1" required value={revisedQty} onChange={e => setRevisedQty(e.target.value)} className="bg-slate-950 border-slate-700" /></div>
            <div><Label htmlFor="inventory-correction-reason">Motivo (opcional)</Label><Input id="inventory-correction-reason" maxLength={500} value={reason} onChange={e => setReason(e.target.value)} className="bg-slate-950 border-slate-700" /></div>
            <DialogFooter><Button type="button" variant="ghost" disabled={revisionBusy} onClick={closeRevision}>Voltar</Button><SaveButton type="submit" disabled={revisionBusy} className="bg-sky-700 text-white">{revisionBusy ? "Salvando..." : "Salvar correção"}</SaveButton></DialogFooter>
          </SaveForm>
        </SaveDialogContent>
      </Dialog>
      <Dialog open={revision?.action === "cancel"} onOpenChange={value => { if (!value) closeRevision(); }}>
        <DialogContent className="bg-slate-900 border-slate-700 text-slate-100" onKeyDown={e => { if (e.key === "Enter") e.preventDefault(); }}>
          <DialogHeader><DialogTitle>Cancelar contagem pendente?</DialogTitle></DialogHeader>
          <p>{revision?.item.part_desc}: a contagem ficará no histórico e não poderá ser aprovada. O estoque não será alterado.</p>
          <div><Label htmlFor="inventory-cancel-reason">Motivo (opcional)</Label><Input id="inventory-cancel-reason" maxLength={500} value={reason} onChange={e => setReason(e.target.value)} className="bg-slate-950 border-slate-700" /></div>
          <DialogFooter><Button variant="ghost" disabled={revisionBusy} onClick={closeRevision}>Voltar</Button><Button disabled={revisionBusy} onClick={saveRevision} className="bg-rose-700 text-white">{revisionBusy ? "Cancelando..." : "Confirmar cancelamento"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={!!countOpen} onOpenChange={(v) => !v && setCountOpen(null)}>
        <DialogContent className="bg-slate-900 border-slate-700 text-slate-100">
          <DialogHeader><DialogTitle className="font-display text-2xl">Contar peça</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Peça</Label>
              <Select value={partId} onValueChange={setPartId}>
                <SelectTrigger data-testid="select-inventory-part" className="bg-slate-950 border-slate-700 mt-1"><SelectValue placeholder="Selecione a peça" /></SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-700 text-slate-100 max-h-72">{parts.map((p) => <SelectItem key={p.id} value={p.id}>{p.descricao} ({p.codigo_interno})</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Quantidade física contada</Label><Input data-testid="input-inventory-qty" type="number" value={qtd} onChange={(e) => setQtd(e.target.value)} className="bg-slate-950 border-slate-700 mt-1" /></div>
          </div>
          <DialogFooter><Button variant="ghost" onClick={() => setCountOpen(null)}>Cancelar</Button><Button data-testid="btn-submit-count" onClick={submitCount} className="bg-sky-700 hover:bg-sky-600">Comparar</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
